// ============================================================
// dev-core-web/src/lib/storage.ts
// Version: 1.1.0
// Purpose:
//   - typed обёртка над Web Storage API (localStorage / sessionStorage)
//   - namespaced keys, чтобы не пересекаться с чужими данными
//   - version-тег для безопасной инвалидации при breaking change
//   - обработка quota/parse errors без падения UI
//   - НЕ хранит access tokens, realtime tickets, classified data
//   - НЕ является источником истины; сервер остаётся authority
//
// Правила:
//   - только скалярные/JSON-сериализуемые значения
//   - TTL опционален, проверяется на чтении
//   - sessionStorage — для per-session данных (например, текущий tenant)
//   - localStorage — для персистентных UI-настроек
//   - секреты и access tokens — НЕ здесь (SECURITY.md §9.3)
//
// Ссылки:
//   - docs/SECURITY.md §9, §35
//   - docs/API.md §4.2, §13.2
//   - docs/CLASSIFICATION.md §14
//   - docs/adr/0002-envelope-vs-kms.md §34
//   - docs/adr/0006-field-level-masking.md §24
// ============================================================

// ------------------------------------------------------------
// Namespace
// ------------------------------------------------------------
//
// Единый префикс, чтобы не пересекаться с чужими ключами
// (аналитика, расширения, виджеты). Формат:
//   dev-core:<version>:<area>:<key>
//
// Версия в префиксе — для безопасной инвалидации при
// несовместимом изменении формата. При breaking change
// достаточно поменять STORAGE_VERSION.

const STORAGE_VERSION = 'v1';
const NAMESPACE = `dev-core:${STORAGE_VERSION}`;

export type StorageArea = 'local' | 'session';

// ------------------------------------------------------------
// Area resolution
// ------------------------------------------------------------
//
// SSR/тесты могут не иметь window/storage. Возвращаем null,
// а вызывающий код получает безопасное поведение (no-op).

function resolveArea(area: StorageArea): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const storage = area === 'local' ? window.localStorage : window.sessionStorage;
    // Проверка доступности: Safari private mode и некоторые
    // окружения бросают при любом обращении к setItem.
    // Одноразовая проверка позволяет отдать null и не падать.
    if (typeof storage === 'undefined' || storage === null) return null;
    return storage;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------
// Envelope для хранения
// ------------------------------------------------------------
//
// Храним не сырое значение, а обёртку с версией и TTL.
// Это позволяет:
//   - обнаружить устаревшую схему и не парсить как новую;
//   - проверять TTL на чтении без фонового cleanup;
//   - отличать «нет значения» от «значение null».

interface StorageEnvelope<T> {
  v: number;                        // schema version
  value: T;
  expiresAt?: number;               // ms since epoch, optional
}

const SCHEMA_VERSION = 1;

// ------------------------------------------------------------
// Safe JSON parse / stringify
// ------------------------------------------------------------
//
// Если что-то повреждено (ручное вмешательство, старый формат),
// НЕ падаем. Возвращаем null и (по желанию вызывающего)
// чистим ключ.

function tryParse<T>(raw: string): StorageEnvelope<T> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const env = parsed as Partial<StorageEnvelope<T>>;
    if (typeof env.v !== 'number') return null;
    if (!('value' in env)) return null;
    return env as StorageEnvelope<T>;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------
// Namespaced key builder
// ------------------------------------------------------------
//
// area отдельно — не входит в key, только в выбор storage.
// key — свободный, не содержит ':' в начале, чтобы не
// создавать двойные разделители.

function buildKey(key: string): string {
  return `${NAMESPACE}:${key}`;
}

// ------------------------------------------------------------
// Public API: read
// ------------------------------------------------------------
//
// TTL проверяется здесь. Если истёк — возвращаем null
// и удаляем запись (lazy cleanup).

export interface ReadOptions {
  area?: StorageArea;
}

export function readJson<T>(
  key: string,
  options: ReadOptions = {},
): T | null {
  const storage = resolveArea(options.area ?? 'local');
  if (!storage) return null;

  const namespaced = buildKey(key);

  let raw: string | null = null;
  try {
    raw = storage.getItem(namespaced);
  } catch {
    return null;
  }

  if (raw === null) return null;

  const envelope = tryParse<T>(raw);
  if (!envelope) {
    // Повреждённое значение — удаляем, чтобы не мешало.
    safeRemove(namespaced, storage);
    return null;
  }

  // Schema version mismatch — не парсим как новую схему.
  if (envelope.v !== SCHEMA_VERSION) {
    safeRemove(namespaced, storage);
    return null;
  }

  // TTL check
  if (typeof envelope.expiresAt === 'number' && envelope.expiresAt <= Date.now()) {
    safeRemove(namespaced, storage);
    return null;
  }

  return envelope.value;
}

// ------------------------------------------------------------
// Public API: write
// ------------------------------------------------------------
//
// ttlMs — опциональный TTL. Если не указан — значение
// персистится без срока.
//
// Если квота исчерпана (QuotaExceededError), не падаем.
// Логика «что делать» — на вызывающем.

export interface WriteOptions {
  area?: StorageArea;
  ttlMs?: number;
}

export function writeJson<T>(
  key: string,
  value: T,
  options: WriteOptions = {},
): boolean {
  const storage = resolveArea(options.area ?? 'local');
  if (!storage) return false;

  const envelope: StorageEnvelope<T> = {
    v: SCHEMA_VERSION,
    value,
  };
  if (typeof options.ttlMs === 'number' && options.ttlMs > 0) {
    envelope.expiresAt = Date.now() + options.ttlMs;
  }

  const namespaced = buildKey(key);

  try {
    storage.setItem(namespaced, JSON.stringify(envelope));
    return true;
  } catch {
    // QuotaExceededError, security error, private mode и т.п.
    // Fail-safe: операция не удалась, состояние не изменилось.
    return false;
  }
}

// ------------------------------------------------------------
// Public API: remove
// ------------------------------------------------------------

export interface RemoveOptions {
  area?: StorageArea;
}

export function remove(key: string, options: RemoveOptions = {}): void {
  const storage = resolveArea(options.area ?? 'local');
  if (!storage) return;
  safeRemove(buildKey(key), storage);
}

function safeRemove(namespaced: string, storage: Storage): void {
  try {
    storage.removeItem(namespaced);
  } catch {
    // Даже remove может бросить в редких окружениях — игнорируем.
  }
}

// ------------------------------------------------------------
// Public API: clear namespace
// ------------------------------------------------------------
//
// Удаляет только ключи нашего namespace. Не трогает
// ничего чужого.

export function clearNamespace(options: RemoveOptions = {}): void {
  const storage = resolveArea(options.area ?? 'local');
  if (!storage) return;

  const prefix = `${NAMESPACE}:`;
  const toRemove: string[] = [];

  try {
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k !== null && k.startsWith(prefix)) {
        toRemove.push(k);
      }
    }
  } catch {
    return;
  }

  for (const k of toRemove) {
    safeRemove(k, storage);
  }
}

// ------------------------------------------------------------
// Typed keys registry
// ------------------------------------------------------------
//
// Единый реестр ключей. Помогает избежать коллизий и делает
// grep-able: где какой ключ используется.
//
// ВАЖНО: этот реестр — не контракт с сервером. Это
// client-only. Поменять его безопасно, если обновить все
// использования.
//
// НЕ добавлять сюда: access tokens, refresh tokens,
// realtime tickets, clearance, classified data.

export const StorageKeys = {
  // UI preferences
  THEME: 'ui:theme',                           // 'light' | 'dark' | 'system'
  SIDEBAR_COLLAPSED: 'ui:sidebar-collapsed',   // boolean
  LOCALE: 'ui:locale',                         // string
  CLEARANCES_UI: 'ui:clearances',             // view mode clearances ('table' | 'timeline')

  // Last-selected context (для UX, не для security)
  LAST_TENANT_ID: 'context:last-tenant-id',
  LAST_ENVIRONMENT: 'context:last-environment',

  // Copilot UI state (не история сообщений —
  // история живёт на сервере, COPILOT.md §20)
  COPILOT_TAB: 'copilot:tab',                  // 'chat' | 'analysis'
  COPILOT_SESSION_HINT: 'copilot:session-hint', // текущий sessionId (для resume)

  // Filters (UX)
  AUDIT_FILTERS: 'filters:audit',
  INVENTORY_FILTERS: 'filters:inventory',
  FLEET_FILTERS: 'filters:fleet',
  INCIDENTS_FILTERS: 'filters:incidents',
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

// ------------------------------------------------------------
// Typed helpers для часто используемых значений
// ------------------------------------------------------------
//
// Не обязательны, но уменьшают количество `as` на местах.

export function readString(
  key: StorageKey,
  options?: ReadOptions,
): string | null {
  const value = readJson<unknown>(key, options);
  return typeof value === 'string' ? value : null;
}

export function writeString(
  key: StorageKey,
  value: string,
  options?: WriteOptions,
): boolean {
  return writeJson(key, value, options);
}

export function readBoolean(
  key: StorageKey,
  options?: ReadOptions,
): boolean | null {
  const value = readJson<unknown>(key, options);
  return typeof value === 'boolean' ? value : null;
}

export function writeBoolean(
  key: StorageKey,
  value: boolean,
  options?: WriteOptions,
): boolean {
  return writeJson(key, value, options);
}

// ------------------------------------------------------------
// Safe removal for auth-related state
// ------------------------------------------------------------
//
// При logout / 401 / revoke — нужно стереть персистентные
// UI-данные. Это НЕ доступ к токенам (их тут нет), но
// безопаснее почистить весь namespace.
//
// Хук `setUnauthorizedHandler` в lib/queryClient.ts может
// вызывать эту функцию.

export function clearClientStorage(): void {
  clearNamespace({ area: 'local' });
  clearNamespace({ area: 'session' });
}